import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";

import {
  MuiCheckIcon,
  MuiFormatBoldIcon,
  MuiFormatItalicIcon,
  MuiFormatListBulletedIcon,
  MuiFormatListNumberedIcon,
  MuiFormatUnderlinedIcon,
  MuiLinkIcon,
  MuiStrikethroughSIcon,
} from "@/components/ui/mui-editor-icons";
import { MuiClearIcon } from "@/components/ui/mui-toolbar-icons";

import {
  MUI_FIELD_LABEL,
  MUI_FIELD_LABEL_FOCUSED,
  MUI_FIELD_LABEL_RESTING,
  MUI_FIELD_LABEL_TRANSITION,
  MUI_FIELD_NOTCHED_TRIGGER,
  MUI_FIELD_OUTLINE_PEER,
} from "@/components/ui/mui-field-shape";
import { FieldNotch } from "@/components/ui/field-notch";
import { OutlinedField } from "@/components/ui/outlined-field";
import { RICH_TEXT_PAINT } from "@/components/ui/rich-text-paint";
import { scrubPastedHtml } from "@/lib/rich-text";
import { isLikelyUrl } from "@/lib/urls";
import { toolbarButtonClasses } from "@/components/ui/rich-text-toolbar-button";
import { cn } from "@/lib/utils";

// The donor's rich-text description control, ported.
//
// Source: `timmy/src/components/fields/RichTextField.tsx` (an MUI `FormControl`
// + shrunk `InputLabel` around a bordered box) wrapping
// `timmy/src/components/richTextEditor/RichTextEditor.tsx` — a draft-js editor
// whose toolbar is exactly seven commands (bold, italic, underline,
// strikethrough, bulleted list, numbered list, link) and whose value crosses its
// seam as **HTML**, via `importStateFromHTML` / `exportStateToHTML`.
//
// **Why not draft-js.** The value's seam is HTML in and HTML out, and draft-js's
// own model never leaves the component — so the editor engine is an
// implementation detail of the donor, not part of the behavior being ported.
// draft-js is also unmaintained and React-18-hostile. What ports is the
// observable control: those seven commands, the outlined frame, and HTML on the
// wire. A `contentEditable` under `document.execCommand` writes `<b> <i> <u>
// <strike> <ul>/<ol><li> <a href>` with no dependency at all — the same
// rendering as draft-js's exporter, **not** the same markup: that one writes
// `<strong> <em> <u> <del>`, a newline after each block and `target="_blank"`
// on every link (`draft-js-export-html` `stateToHTML.js:48-56`). Only a length
// counted over the stored HTML can tell them apart — the community link's
// 500-character description (#754), where the owner accepted the difference
// (#845 D3). `execCommand` is soft-deprecated and universally
// implemented; the alternative — a full editing engine — is a dependency
// decision, not a parity one.
//
// **Paste keeps its formatting, reduced to draft-js's vocabulary.** The donor
// runs a paste through its importer, which keeps the styles its own model has
// and discards the rest: `b`/`strong`, `i`/`em`, `u`/`ins`, `code`, `s`/`del`
// as inline styles, `li`/`blockquote`/`h1`-`h6`/`pre`/`figure` as blocks, and
// everything else falling through to `unstyled`
// (`draft-js-import-element/lib/stateFromElement.js:235-291,593-632`). So modsy
// flattens a pasted table, a `<sub>` or a styled `<span>` to text — and a
// pasted `<strike>` keeps its characters and loses its line.
// `scrubPastedHtml` is now that same reduction, held to
// `RICH_TEXT_PASTE_TAGS`; unknown tags unwrap rather than drop, so nothing a
// paste carried is silently deleted.
//
// **Two lists, two questions, and only the paste path narrowed.** #292 ratified
// the *stored* tag set with tables and `sub`/`sup` in it, and that set still
// governs what a description may contain: a migrated row that arrives with a
// table renders its table, here and on the read side, and editing the
// description does not destroy it — which is the one thing modsy does that this
// deliberately does not, because reproducing it would mean deleting content on
// load. What the paste path no longer does is *introduce* markup modsy could
// never have produced. Owner ruling D3 on #410, scoped exactly this way.

/**
 * The toolbar, in the donor's order and its groups.
 *
 * `RichTextEditor.tsx` renders `inlineStyles`, a vertical `Divider`,
 * `blockStyles`, another `Divider`, then the link control — and its block
 * styles are ordered-then-unordered, not the other way round.
 */
const INLINE_COMMANDS: { label: string; command: string; icon: ReactNode }[] = [
  { label: "Bold", command: "bold", icon: <MuiFormatBoldIcon /> },
  { label: "Italic", command: "italic", icon: <MuiFormatItalicIcon /> },
  { label: "Underline", command: "underline", icon: <MuiFormatUnderlinedIcon /> },
  { label: "Strikethrough", command: "strikeThrough", icon: <MuiStrikethroughSIcon /> },
];

const BLOCK_COMMANDS: { label: string; command: string; icon: ReactNode }[] = [
  {
    label: "Numbered list",
    command: "insertOrderedList",
    icon: <MuiFormatListNumberedIcon />,
  },
  { label: "Bulleted list", command: "insertUnorderedList", icon: <MuiFormatListBulletedIcon /> },
];

/**
 * Draft-js's block vocabulary, as `DefaultDraftBlockRenderMap` renders it into
 * the editor: `unstyled` is a `div`, the six headers are `h1`-`h6`, both list
 * items are `li`, `blockquote` is itself, `code-block` is a `pre` and `atomic`
 * a `figure`. `p` is here for the read side's export, which is what a saved
 * description arrives as.
 *
 * The editor's own root is a `div`, so a selection in bare content with no
 * wrapper resolves to the root — which is right: that content *is* one
 * unstyled block.
 */
const BLOCK_ELEMENTS = "li, p, div, h1, h2, h3, h4, h5, h6, blockquote, pre, figure";

/**
 * The link the selection is on, if any — **within the one block it starts in**.
 *
 * The donor walks the selected offsets for a `LINK` entity
 * (`RichTextEditor.tsx:243-259`), which catches both a caret sitting *inside* a
 * link and a selection drawn *across* one; `closest` alone only catches the
 * first, and selecting the linked words is the ordinary way to re-open the box
 * on them.
 *
 * But it walks **one block**, and so does the lit state beside it — and on a
 * backward drag the two disagree about which. `RichUtils.currentBlockContainsLink`
 * slices the character list of `getBlockForKey(selection.getAnchorKey())`
 * (`RichTextEditorUtil.js:22-30`) — where the drag *began* — while the prefill
 * loop reads `getBlockForKey(selection.getStartKey())`, the earlier block. The
 * same block for the ordinary forward drag; opposite ends of an upward one. So
 * in modsy, dragging up out of a linked paragraph lights Link and then opens an
 * empty box, and dragging down out of one leaves it grey.
 *
 * `where` is that key. A DOM `Range` is always in document order and cannot
 * express a direction at all, so the caller supplies the node whose block to
 * read: the selection's *anchor* for the lit state, the range's start for the
 * prefill. Searching every anchor the range happened to touch instead lit the
 * control from a paragraph the selection merely ran through, and then offered
 * that paragraph's address as the one about to be edited.
 */
function linkInRange(range: Range | null, where?: Node | null): HTMLAnchorElement | null {
  if (!range) return null;
  const start = where ?? range.startContainer;
  const from = start instanceof Element ? start : start.parentElement;
  const block = from?.closest(BLOCK_ELEMENTS);
  if (!block) return null;
  const closest = from!.closest("a");
  if (closest && block.contains(closest)) return closest;
  for (const anchor of block.querySelectorAll("a")) {
    if (range.intersectsNode(anchor)) return anchor;
  }
  return null;
}

const TAG_TO_INLINE_COMMAND: Record<string, string> = {
  B: "bold",
  STRONG: "bold",
  I: "italic",
  EM: "italic",
  U: "underline",
  INS: "underline",
  S: "strikeThrough",
  DEL: "strikeThrough",
};

/**
 * The allowlisted tags (`RICH_TEXT_TAGS`) that the importer treats as inline
 * (`draft-js-import-element`'s `INLINE_ELEMENTS`), plus `INS`, which it maps.
 * Any other element is a block, and each block starts the importer's style
 * stack afresh (`stateFromElement.js:343-345`) — so the styles a character
 * carries come only from the inline elements between it and its block.
 */
const IMPORTER_INLINE_TAGS = new Set([
  "A",
  "B",
  "BR",
  "DEL",
  "EM",
  "I",
  "INS",
  "S",
  "SPAN",
  "STRIKE",
  "STRONG",
  "SUB",
  "SUP",
  "U",
]);

/**
 * The controls the donor's toolbar lights for a value it has just been handed.
 *
 * `importStateFromHTML` builds a new `EditorState`, whose selection is
 * collapsed at offset 0 of the first block (`EditorState.js:66-72`), and the
 * toolbar reads that selection whether or not the editor has focus. So it
 * reads the first character the importer keeps:
 *
 *   - `draft-js-import-element` maps space, tab and newline to spaces, trims
 *     them from each block's ends and drops a block left empty
 *     (`stateFromElement.js:208-212,522-533`), so across the value that is the
 *     first character that is none of those three — `&nbsp;` and `<br>`
 *     included, since neither collapses — and text straight under the root is
 *     a paragraph of its own,
 *   - a list control is lit when that character's block is one of its items
 *     (`getCurrentBlockType`, `RichTextEditorUtil.js:31-33`),
 *   - an inline control is lit when that character carries the style — a caret
 *     at offset 0 takes its first character's (`EditorState.js:550-551`) —
 *     read through the tags the importer maps: `b`/`strong`, `i`/`em`,
 *     `u`/`ins`, `s`/`del` (`stateFromElement.js`, `addStyleFromTagName`),
 *   - Link is never lit: `currentBlockContainsLink` slices the characters a
 *     collapsed selection covers, which is none (`RichTextEditorUtil.js:26`).
 */
function controlsAtContentStart(node: HTMLElement): Set<string> {
  const lit = new Set<string>();
  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode: (candidate) => {
      if (candidate.nodeType === Node.TEXT_NODE) {
        return /[^ \t\n]/.test(candidate.textContent ?? "")
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_SKIP;
      }
      return (candidate as Element).tagName === "BR"
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_SKIP;
    },
  });
  const first = walker.nextNode();
  if (!first) return lit;
  // Up from the character: its styles are the inline elements passed on the
  // way to its block. The block type is the nearest block's, where a plain
  // block inherits its list item's and a heading keeps its own
  // (`stateFromElement.js:330-336`); a `li` is numbered under an `ol` and
  // bulleted anywhere else (`getBlockTypeFromTagName`).
  let inBlock = true;
  for (let el = first.parentElement; el && el !== node; el = el.parentElement) {
    if (IMPORTER_INLINE_TAGS.has(el.tagName)) {
      const command = TAG_TO_INLINE_COMMAND[el.tagName];
      if (inBlock && command) lit.add(command);
      continue;
    }
    inBlock = false;
    if (/^H[1-6]$/.test(el.tagName)) break;
    if (el.tagName === "LI") {
      lit.add(el.parentElement?.tagName === "OL" ? "insertOrderedList" : "insertUnorderedList");
      break;
    }
  }
  return lit;
}

/**
 * Every anchor in the field wears exactly the `target` the donor's export gives
 * it — and nothing else wears one.
 *
 * The donor writes `target="_blank"` on *export*, not on the Link command:
 * `stateToHTML`'s `entityStyleFn` rewrites every LINK entity in the document as
 * `{ href, target: '_blank' }` on its way to HTML (`draftJsUtils.tsx:36-48`),
 * and its in-editor decorator renders the same `target` while you are typing
 * (`:20-27`). So it is a property of the whole document at the seam, not of the
 * anchor the toolbar just made — which is why this runs on every emit rather
 * than after `createLink`, and why a link that arrives without the attribute
 * picks it up on the first edit exactly as it would in the source.
 *
 * The rule is **total**, both halves of it. Draft-js has no way to hold a
 * `target` without a link — an entity is its `url` — so an anchor with no
 * `href` must carry no `target` either. `contentEditable` can hold one: the
 * paste scrub drops a `javascript:` href and leaves the stripped anchor behind
 * (`scrubPastedHtml`), and stamping only `a[href]` let that anchor keep the
 * `target="victim-frame"` it was pasted with.
 *
 * The `rel` rides along, and it is the *decorator's*, not the exporter's. The
 * donor's two seams disagree on purpose: the decorator renders
 * `rel='noopener noreferrer'` on every link you can see in the editor
 * (`draftJsUtils.tsx:20-27`), while the exporter writes `{ href, target }` and
 * no `rel` at all (`:36-48`) — so a modsy author's live editor never hands a
 * target page the referrer or an `opener` handle, and a *saved* description
 * carries none, because `nh3`'s `link_rel` puts one back at the seam that
 * renders it into a page (`server/app/core/rich_text.py`). One DOM serves both
 * seams here, so the attribute goes on the nodes and `exportedHtml` takes it
 * back off the string.
 */
function normaliseAnchors(node: HTMLElement): void {
  for (const anchor of node.querySelectorAll("a")) {
    if (anchor.hasAttribute("href")) {
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
    } else {
      anchor.removeAttribute("target");
      anchor.removeAttribute("rel");
    }
  }
}

/**
 * The document as the donor's *exporter* writes it — the view-only `rel` off.
 *
 * Read straight from the live node when there is nothing to take off, which is
 * every description without a link and every keystroke in one; the copy is made
 * only when a link is actually present.
 */
function exportedHtml(node: HTMLElement): string {
  if (!node.querySelector("a[rel]")) return node.innerHTML;
  const copy = node.cloneNode(true) as HTMLElement;
  for (const anchor of copy.querySelectorAll("a[rel]")) anchor.removeAttribute("rel");
  return copy.innerHTML;
}

interface InsertPoint {
  asText?: boolean;
  /** Viewport coordinates to drop at, when the gesture has any. */
  at?: { x: number; y: number };
}

/**
 * The caret position under a point — draft-js's `getSelectionForEvent`
 * (`DraftEditorDragHandler.js:44-51`), which is how a drop knows where it
 * landed.
 *
 * Two spellings, because the standard one is `caretPositionFromPoint` and
 * WebKit only ever shipped `caretRangeFromPoint`. draft-js checks for exactly
 * these two, in this order.
 */
function caretRangeAt({ x, y }: { x: number; y: number }): Range | null {
  if (typeof document.caretPositionFromPoint === "function") {
    const position = document.caretPositionFromPoint(x, y);
    if (!position) return null;
    const range = document.createRange();
    range.setStart(position.offsetNode, position.offset);
    range.collapse(true);
    return range;
  }
  const webkit = document as Document & {
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  return webkit.caretRangeFromPoint?.(x, y) ?? null;
}

/**
 * An editor holding nothing, in either of the two shapes `contentEditable`
 * leaves behind. The donor's exporter normalises the same thing
 * (`draftJsUtils.tsx`), and the payload rule downstream depends on it: an empty
 * description must be *absent* from the patch, not saved as a blank paragraph.
 */
const EMPTY_DOCUMENT = /^(<br\s*\/?>|<p>(<br\s*\/?>)*<\/p>)$/i;

/**
 * draft-js's `ContentState.hasText()`, read off the editor's DOM: more than one
 * block, or a first block with characters in it (`ContentState.js:137-141`,
 * zero-width spaces aside). A list item is a block of its own, as it is in
 * draft-js's model, and so is anything else at the top level. It is what the
 * donor's placeholder keys on — and the only thing: no block-type rule exists,
 * so an empty list item still has no text (`DraftEditor.react.js:466-467`).
 */
function editorHasText(node: HTMLElement): boolean {
  if ((node.textContent ?? "").replace(/\u200b/g, "").length > 0) return true;
  let blocks = 0;
  for (const child of node.childNodes) {
    if (child instanceof HTMLUListElement || child instanceof HTMLOListElement) {
      blocks += Math.max(child.querySelectorAll("li").length, 1);
    } else {
      blocks += 1;
    }
  }
  return blocks > 1;
}

/** The HTML void elements: an open tag that never takes a close. */
const VOID_TAGS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "source",
  "track",
  "wbr",
]);

/**
 * `editorHasText`, read off the stored string rather than the editor's DOM —
 * for the first render, which has no editor yet (on the server, or before
 * mount), so a stored empty document (`<p><br></p>`, `<ul><li><br></li></ul>`,
 * what modsy's exporter writes for an emptied field) shows the placeholder from
 * the first paint, and the server's markup and the hydrating client's agree.
 * The same two tests in one scan of the string — any text but zero-width
 * spaces, else more than one top-level block, a list counting its items. It
 * only reads the string; nothing it walks is ever rendered. The editor's DOM
 * takes over after mount, so a string the scan reads loosely costs one render
 * at most.
 */
function htmlHasText(html: string): boolean {
  let blocks = 0;
  let depth = 0;
  let inList = false;
  let items = 0;
  let at = 0;
  while (at < html.length) {
    if (html.startsWith("<!--", at)) {
      const end = html.indexOf("-->", at + 4);
      at = end === -1 ? html.length : end + 3;
      continue;
    }
    const tag = html[at] === "<" ? TAG.exec(html.slice(at)) : null;
    if (!tag) {
      // A character of text — any but a zero-width space, however spelled.
      const zeroWidth = ZERO_WIDTH.exec(html.slice(at, at + 20));
      if (!zeroWidth) return true;
      at += zeroWidth[0].length;
      continue;
    }
    at += tag[0].length;
    const [, closing, name = ""] = tag;
    const element = name.toLowerCase();
    if (closing) {
      depth = Math.max(depth - 1, 0);
      if (inList && depth === 0) {
        blocks += Math.max(items, 1);
        inList = false;
      }
    } else if (VOID_TAGS.has(element)) {
      if (depth === 0) blocks += 1;
    } else {
      if (depth === 0) {
        if (element === "ul" || element === "ol") {
          inList = true;
          items = 0;
        } else {
          blocks += 1;
        }
      } else if (inList && element === "li") {
        items += 1;
      }
      depth += 1;
    }
  }
  if (inList) blocks += Math.max(items, 1);
  return blocks > 1;
}

/** An open or close tag at the start of the rest of the string. */
const TAG = /^<(\/?)([a-zA-Z][\w-]*)[^>]*>/;
/** A zero-width space at the start of the rest: the character or its entities. */
const ZERO_WIDTH = /^(?:\u200b|&#8203;|&#x200b;|&ZeroWidthSpace;)/i;

export interface RichTextFieldProps {
  label: string;
  /** HTML. The same shape that comes back out of `onChange`. */
  value: string;
  onChange: (html: string) => void;
  required?: boolean;
  /**
   * **Greys the frame and its label, and nothing else.**
   *
   * Not an oversight, and not a shortcut: the donor's `RichTextField` sets
   * `disabled` on the `FormControl` and passes **none** of it down to
   * `RichTextEditor` (`RichTextField.tsx:49-58`), which does not read the
   * `FormControl` context either. So a submit in flight greys the label while
   * the editor and its toolbar stay live, and Description is the one field on
   * the item edit form you can keep typing into while it saves.
   */
  disabled?: boolean;
  error?: string;
  helperText?: string;
  /**
   * The grey hint at the empty editor's first line — the donor's
   * `placeholder` (`RichTextField.tsx:17,53`), the links dialog's "Add a
   * description". See the placeholder's render below.
   */
  placeholder?: string;
  className?: string;
  "data-testid"?: string;
}

export function RichTextField({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  error,
  helperText,
  placeholder,
  className,
  "data-testid": testId,
}: RichTextFieldProps) {
  const editorId = useId();
  const messageId = `${editorId}-message`;
  const placeholderId = `${editorId}-placeholder`;
  /**
   * Whether the editor holds text in draft-js's sense (`editorHasText`) — the
   * placeholder's one condition. Seeded from the value, because the editor is
   * empty until the effect below writes into it; re-read off the DOM after
   * every write and every emit.
   */
  const [hasText, setHasText] = useState(() => htmlHasText(value));
  const editor = useRef<HTMLDivElement | null>(null);
  /**
   * The last HTML this component put *into* the editor, so a controlled
   * re-render with the same value does not rewrite `innerHTML` and drop the
   * caret back to the start.
   *
   * It starts empty, not at `value` — the editor starts empty too, and seeding
   * this with the initial value made the mount effect below decide there was
   * nothing to write, so a field opened with existing HTML rendered blank.
   */
  const emitted = useRef<string>("");
  /**
   * The box, and whether the last mousedown anywhere on the page landed in it.
   *
   * **This is the donor's whole notion of focus for this field.**
   * `RichTextField.tsx:27-41` installs a document `mousedown` listener and sets
   * `editorFocused` from `inputRef.current.contains(e.target)` — the container,
   * not the editor — and that one flag drives both the 2px primary fieldset
   * (`:96-101`) and the label's `focused` (`:50`). There is no focus or blur
   * handler anywhere in the file, and `RichTextEditor` draws no focus style of
   * its own, so the observable rules are: tabbing in from Item Link paints
   * nothing; pressing the 16.5px inset or the toolbar's empty right-hand space
   * paints blue even though nothing focusable was hit; and tabbing back out
   * leaves it blue until the next press elsewhere. `:focus-within` gets none of
   * those three right, which is why `OutlinedField` is asked for the
   * hand-rolled outline below.
   */
  const box = useRef<HTMLDivElement | null>(null);
  const [boxFocused, setBoxFocused] = useState(false);
  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      const node = box.current;
      setBoxFocused(node !== null && event.target instanceof Node && node.contains(event.target));
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, []);
  /**
   * Whether the address card is showing — **not** whether it holds anything.
   *
   * The donor keeps the two apart, and the difference is visible: its
   * `closeIfOutside` sets `linkInputToggled` false and never touches `linkUrl`
   * (`RichTextEditor.tsx:223-236`), while ✕ and confirm both clear it
   * (`:264-276`). So clicking away mid-address and pressing Link again returns
   * you to what you had typed. Folding the two into one nullable draft threw
   * the address away on every stray click.
   */
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkDraft, setLinkDraft] = useState("");
  /** Whether the draft is the user's typing rather than a seeded address — the
   * donor's `error` key, which only `handleUrlChange` ever writes. */
  const [linkEdited, setLinkEdited] = useState(false);
  /** Whether the address box holds the caret — half of MUI's `notched` rule. */
  const [linkFocused, setLinkFocused] = useState(false);
  /**
   * Whether the selection inside the editor covers any text.
   *
   * The donor disables its Link button on a collapsed selection
   * (`RichTextEditor.tsx`: `disabled={editorState.getSelection().isCollapsed()}`)
   * — there is nothing to make into a link, and `createLink` over a caret does
   * nothing either. Tracked on `selectionchange` because a selection is not a
   * React input: nothing re-renders when the caret moves.
   */
  const [hasSelection, setHasSelection] = useState(false);
  /**
   * The commands that apply where the caret is.
   *
   * The donor tints an active control `primary.main` against the default grey
   * (`ToolbarIcon`'s `toggled`, fed by `hasInlineStyle` / `isBlockType` /
   * `currentBlockContainsLink`), so a typist can see that they are already
   * inside a bold run. `queryCommandState` is `execCommand`'s answer to the same
   * question, read on the same `selectionchange` that tracks the selection.
   */
  const [active, setActive] = useState<ReadonlySet<string>>(new Set());
  /**
   * The last selection that was *inside* the editor.
   *
   * Draft-js's `editorState` is this: `editOnBlur` clears `hasFocus` and leaves
   * the `SelectionState`'s offsets alone, so the donor's toolbar and its
   * `RichUtils` commands keep answering about the run of text you selected long
   * after the caret has gone somewhere else. `execCommand` has no such memory —
   * it acts on the live DOM selection, and clicking into the address input
   * moves it — so the equivalent is kept here by hand.
   */
  const savedRange = useRef<Range | null>(null);
  /** Whether the fragment being dragged came from this editor — draft-js's
   * `_internalDrag`, and the difference between a move and a paste. */
  const internalDrag = useRef(false);
  /** The Link control and its card, for the click that closes the card. */
  const linkBox = useRef<HTMLDivElement | null>(null);

  /**
   * A value pushed in from outside — a form reset, or another item loaded into
   * the same field.
   *
   * The donor answers it with `setEditorState(importStateFromHTML(value))`
   * (`RichTextEditor.tsx:32-41`), and that is a whole new `EditorState`: a
   * `createWithContent` carries `SelectionState.createEmpty`, collapsed at the
   * start of the new content with `hasFocus: false`. Every toolbar control
   * reads that selection, focus or not, so the toolbar lights for the new
   * content's first block — a list's control, the first character's styles
   * (`controlsAtContentStart`) — on arrival and on every value pushed in. The
   * remembered range goes: it points into nodes that have just left the
   * document, and holding it left the buttons lit for text that no longer
   * exists, over a range `execCommand` would silently rebase onto whatever the
   * caret happened to be next to.
   *
   * `importStateFromHTML` also attaches the link decorator, so the anchors are
   * shielded on arrival rather than at the next keystroke — `normaliseAnchors`
   * is that half. `emitted.current` stays the value as *given*: the decorator
   * is a view, and it does not make the field dirty.
   */
  useEffect(() => {
    const node = editor.current;
    if (!node || value === emitted.current) return;
    node.innerHTML = value;
    normaliseAnchors(node);
    emitted.current = value;
    setHasText(editorHasText(node));
    savedRange.current = null;
    setHasSelection(false);
    setActive(controlsAtContentStart(node));
  }, [value]);

  /**
   * Re-read what the selection is and which commands apply to it.
   *
   * Called on `selectionchange` and again straight after every command, because
   * `execCommand` changes what `queryCommandState` answers without moving the
   * caret — so on a collapsed selection the browser fires no `selectionchange`
   * at all, and a toolbar that only listens for that keeps showing the old
   * state until the typist moves the caret themselves. They do that by typing,
   * which is why the toggle looked like it lagged its own click.
   */
  const readSelectionState = useCallback(() => {
    const node = editor.current;
    const selection = document.getSelection();
    const inside =
      node !== null &&
      selection !== null &&
      selection.anchorNode !== null &&
      node.contains(selection.anchorNode);

    // **A selection that has left the editor changes nothing.** Draft-js keeps
    // its `SelectionState` across a blur (`editOnBlur.js` sets `hasFocus:
    // false` and nothing else), so the donor's toolbar holds its lit commands
    // and its enabled Link while the caret sits in the address box. Recomputing
    // from a selection somewhere else emptied the toolbar — and disabled the
    // Link control while its own card was open, over the very text the card is
    // about to link.
    if (!inside) return;

    savedRange.current = selection!.rangeCount > 0 ? selection!.getRangeAt(0).cloneRange() : null;
    setHasSelection(!selection!.isCollapsed);
    const on = new Set<string>();
    for (const { command } of [...INLINE_COMMANDS, ...BLOCK_COMMANDS]) {
      try {
        if (document.queryCommandState(command)) on.add(command);
      } catch {
        // Some engines throw on an unsupported command rather than answering
        // false. An unanswerable command is simply not shown as active.
      }
    }
    // **Only a selection *over* a link lights the icon.**
    // `RichUtils.currentBlockContainsLink` slices the block's characters between
    // the selection's start and end offsets (`RichTextEditorUtil.js:22-30`), so
    // a collapsed caret slices nothing and the icon stays grey inside a link —
    // where the control is disabled anyway (`RichTextEditor.tsx:293`), and a
    // disabled control drawn as active is a control offering something it will
    // not do.
    if (!selection!.isCollapsed && selection!.rangeCount > 0) {
      const range = selection!.getRangeAt(0);
      // The *anchor* block — `currentBlockContainsLink` reads `getAnchorKey()`,
      // so which end of an upward drag this is depends on where it began.
      if (
        node.contains(range.commonAncestorContainer) &&
        linkInRange(range, selection!.anchorNode)
      ) {
        on.add("link");
      }
    }
    setActive(on);
  }, []);

  useEffect(() => {
    document.addEventListener("selectionchange", readSelectionState);
    return () => document.removeEventListener("selectionchange", readSelectionState);
  }, [readSelectionState]);

  // **Enter starts a paragraph, not a `<div>`.** The donor exports through
  // `stateToHTML`, which wraps every draft-js block in `<p>`; Chrome's
  // `contentEditable` defaults to `<div>`, and the read side spaces paragraphs
  // and nothing else (`html-box.tsx`, through `RICH_TEXT_READ_PAINT`) — so a
  // multi-paragraph description typed here rendered with its paragraphs run
  // together where a modsy-authored one is spaced.
  useEffect(() => {
    try {
      document.execCommand("defaultParagraphSeparator", false, "p");
    } catch {
      // Unsupported in an engine that does not answer the command; the field
      // still works, its breaks are just the engine's own.
    }
  }, []);

  // The donor's link card closes on a click anywhere outside itself — a
  // `document` `mousedown` listener kept alive for as long as the box is open
  // (`RichTextEditor.tsx:223-236`). Without it the 200px card hangs over the
  // toolbar until Cancel, Apply or Escape.
  useEffect(() => {
    if (!linkOpen) return;
    const onMouseDown = (event: MouseEvent) => {
      if (linkBox.current?.contains(event.target as Node)) return;
      // Closed, not cleared — and the caret is left wherever the click put it,
      // as the donor's `closeIfOutside` does.
      setLinkOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [linkOpen]);

  const emit = useCallback(() => {
    const node = editor.current;
    if (!node) return;
    normaliseAnchors(node);
    // An emptied `contentEditable` keeps a stray `<br>` — inside a paragraph now
    // that the separator is `<p>`. The donor normalises the same shape away
    // (`draftJsUtils.tsx`: `/^<p>(<br\/?>)*<\/p>$/` → `''`), and reporting it as
    // empty is what leaves the field off the payload entirely.
    const exported = exportedHtml(node);
    const html = EMPTY_DOCUMENT.test(exported) ? "" : exported;
    emitted.current = html;
    setHasText(editorHasText(node));
    onChange(html);
  }, [onChange]);

  /**
   * Put transferred content in at the caret.
   *
   * A **paste** keeps what it can: `text/html` goes through `scrubPastedHtml`,
   * which is this port's stand-in for `DraftPasteProcessor.processHTML`
   * (`editOnPaste.js:137`) — the donor keeps the styles its own model has and
   * discards the rest.
   *
   * A **drop** keeps none of it. draft-js's `onDrop` inserts `data.getText()`
   * and has no HTML path at all (`DraftEditorDragHandler.js:77-112`), so
   * dragging a formatted fragment in from another window lands as text on the
   * donor. `contentEditable` would happily take it as live markup, which is why
   * the drop still has to be intercepted rather than left alone.
   */
  const insertTransferred = useCallback(
    (transfer: DataTransfer | null, { asText = false, at }: InsertPoint = {}) => {
      if (!transfer) return;
      editor.current?.focus();
      // **A drop lands under the pointer**, not at whatever the caret was doing
      // before. draft-js computes its `dropSelection` from
      // `getSelectionForEvent` — `caretRangeFromPoint(event.x, event.y)`
      // (`DraftEditorDragHandler.js:44-51`) — and inserts there (`:112`). A
      // paste has no point and passes none.
      //
      // Only when the point resolves to somewhere *in* the field: a drop can be
      // reported at coordinates that hit the toolbar, the frame's padding, or
      // nothing at all, and moving the caret out of the editable would put the
      // insert wherever the caret last was on the page.
      if (at) {
        const range = caretRangeAt(at);
        if (range && editor.current?.contains(range.startContainer)) {
          const selection = window.getSelection();
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      }
      const html = asText ? "" : transfer.getData("text/html");
      if (html) document.execCommand("insertHTML", false, scrubPastedHtml(html));
      else document.execCommand("insertText", false, transfer.getData("text/plain"));
      emit();
    },
    [emit],
  );

  /**
   * Put the caret back on the run of text the toolbar is reporting about.
   *
   * The other half of keeping state across a blur. Draft-js's commands take the
   * `editorState` and apply over the `SelectionState` the blur preserved
   * (`RichTextEditor.tsx:61-74`), so the donor's Bold works on the phrase you
   * selected even after you have clicked into Item Name. `execCommand` acts on
   * the live DOM selection instead, and `focus()` alone returns a *collapsed*
   * caret — every command a silent no-op against a toolbar still advertising
   * the selection.
   *
   * A no-op when the selection never left: `savedRange` is refreshed from every
   * in-editor `selectionchange`, so it is the live range already.
   */
  const restoreSelection = useCallback(() => {
    editor.current?.focus();
    const range = savedRange.current;
    if (!range) return;
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }, []);

  /**
   * Escape closes the card — and only the card.
   *
   * The owner's ruling on #410 (D1) is a named parity break: modsy's URL box
   * handles no keys at all (`RichTextEditor.tsx:300-308`), so there Escape falls
   * through to whatever is listening above it. Here that is the Request Item
   * dialog, and "Escape closes the card" is worth nothing if the same keystroke
   * takes the form with it.
   *
   * **Which is why this cannot live on the input's `onKeyDown`.** Radix's
   * dismissable layer listens on the *document* in the capture phase
   * (`@radix-ui/react-use-escape-keydown`: `addEventListener("keydown",
   * handleKeyDown, { capture: true })`), and capture runs outside-in — so it
   * has already closed the dialog before any React handler on the input is
   * reached, whatever that handler then stops. `window` is the one node above
   * `document` in that order, so a capture listener here is the only place a
   * component can get in front of it. Walked: with the handler on the input,
   * Escape left `document.activeElement` on `<body>` and the whole dialog gone.
   *
   * Scoped as narrowly as a global listener can be — only while the card is
   * open, only for Escape, and only for a keystroke whose target is inside the
   * card — so nothing else on the page loses its Escape.
   *
   * The selection comes back with the close. The card's box is `autoFocus`ed,
   * which collapses the DOM selection the moment it opens, leaving `savedRange`
   * the only copy — so a close that does not restore leaves the caret on
   * `<body>` and the toolbar's Link control dead over the very text it was just
   * opened on. The cross restores for the same reason (`cancelLink`); the
   * outside click is the one close that does not, because there the pointer has
   * already chosen where the caret goes.
   */
  useEffect(() => {
    if (!linkOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (!linkBox.current?.contains(event.target as Node)) return;
      event.preventDefault();
      event.stopPropagation();
      setLinkOpen(false);
      restoreSelection();
    };
    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [linkOpen, restoreSelection]);

  /**
   * Run one `execCommand` over the selection the toolbar is reporting about.
   *
   * `keepCaret` is the difference between the donor's two gestures, and it is
   * not a preference:
   *
   *   - a **toolbar** command is `handleToggleStyle`, which pushes an
   *     `editorState` and touches focus not at all
   *     (`RichTextEditor.tsx:61-74`) — so pressing Bold with the caret in Item
   *     Name styles the remembered selection and leaves you typing in Item
   *     Name. `execCommand` only acts on the *focused* editable, so the focus
   *     is borrowed and handed straight back.
   *   - the **link card's** ✓ calls `editorRef.current.focus()` *before* it
   *     applies (`:318-326`), and draft-js's `focus()` runs
   *     `EditorState.forceSelection` — so confirming a link leaves the editor
   *     focused with the linked run still selected, ready to type over. Handing
   *     the focus back there returns it to an input React is about to unmount,
   *     and it lands on `<body>` with the selection gone.
   */
  const run = useCallback(
    (command: string, argument?: string, { keepCaret = false } = {}) => {
      const away = keepCaret ? null : document.activeElement;
      restoreSelection();
      document.execCommand(command, false, argument);
      emit();
      // Before handing the focus back — this is what refreshes `savedRange`
      // from the selection the command just left behind.
      readSelectionState();
      if (away instanceof HTMLElement && away !== editor.current) away.focus();
    },
    [emit, readSelectionState, restoreSelection],
  );

  const openLinkDraft = () => {
    // **The box opens on the address the selection already has** — and only
    // then. The donor walks the selected offsets for a `LINK` entity and seeds
    // the field with its url (`RichTextEditor.tsx:243-260`), so editing a link
    // starts from what it points at rather than from nothing; with no link in
    // range it seeds nothing at all, which is what leaves a half-typed address
    // where an outside click left it.
    const existing = linkInRange(savedRange.current)?.getAttribute("href");
    if (existing !== null && existing !== undefined) {
      setLinkDraft(existing);
      setLinkEdited(false);
    }
    // **Focused from the card's first frame, because the box is `autoFocus`ed.**
    // Left to the input's own `onFocus`, this flips one commit *after* mount —
    // so the label mounted resting and then travelled its 200ms onto the
    // border, and the notch unfurled, on every open. The donor shows neither:
    // its `InputLabel` reads a `FormControl` that was already `focused` (the
    // editor holds the selection the card needs to exist at all), so it mounts
    // shrunk over a gap that is already cut.
    setLinkFocused(true);
    setLinkOpen(true);
  };

  /**
   * ✕ — close the card and hand the selection back to the editor.
   *
   * The donor's `LinkInputCloseButton` runs `editorRef.current.focus()` before
   * it closes (`RichTextEditor.tsx:312-316`), and draft-js's `focus()` restores
   * the selection its `editorState` still holds — so backing out leaves the run
   * of text still highlighted and the caret where it was. Clearing the draft
   * alone dropped the selection on the floor: the input unmounted, focus fell
   * to `<body>`, and the same text had to be selected again to retry.
   *
   * Its *outside-click* close does not focus, and neither does this one.
   */
  const cancelLink = () => {
    // ✕ clears the address as well as closing — `handleCloseLinkInput`
    // (`RichTextEditor.tsx:264-268`), unlike the outside click above.
    setLinkOpen(false);
    setLinkDraft("");
    setLinkEdited(false);
    restoreSelection();
  };

  const applyLink = () => {
    // **The same gate the donor's confirm has, and no other.** `:318-326` tests
    // `linkUrl.error`, which only `handleUrlChange` writes — so a *seeded*
    // address always applies, whatever the rule would say about it. Re-checking
    // the href here instead made ✓ a dead control on any stored link the rule
    // refuses: enabled, and silently doing nothing, forever. A relative or
    // fragment href fails the donor's own regex too, and the donor still
    // applies it, so this outlives the `mailto:`/`tel:` gap on #408.
    if (linkInvalid) return;
    const href = linkDraft.trim();
    // Confirm clears the address too (`handleUpdateLinkInput`, `:270-276`).
    setLinkOpen(false);
    setLinkDraft("");
    setLinkEdited(false);
    // **An emptied box unlinks.** The donor's confirm is disabled only on a
    // validation error, and an empty value is explicitly not one
    // (`RichTextEditor.tsx:277-285,319-320`) — confirming empty reaches
    // `RichUtils.toggleLink(state, selection, null)` and strips the link
    // (`:76-92`). Refusing an empty href left linked text with no way back out
    // of the toolbar.
    if (!href) {
      run("unlink", undefined, { keepCaret: true });
      return;
    }
    // The donor's link command inserts a plain anchor over the selection, with
    // the address exactly as typed — no scheme is added for you.
    run("createLink", href, { keepCaret: true });
  };

  // The donor marks the URL box in error and disables its confirm button while
  // the value fails `urlValidation` (`RichTextEditor.tsx`, `handleUrlChange`).
  // **Only what the user typed can be invalid.** The donor writes `error` in
  // `handleUrlChange` alone (`RichTextEditor.tsx:277-285`) and seeds the box
  // with `setLinkUrl({ value })` — no `error` key — when it opens on an existing
  // link (`:257-258`). So a stored address the current rule would refuse opens
  // clean and its confirm stays live. Deriving the flag from the draft alone put
  // a red outline and a dead ✓ on such a link, which left it impossible to
  // re-point *or* unlink from the card at all.
  const linkInvalid = linkEdited && linkDraft !== "" && !isLikelyUrl(linkDraft);
  // MUI's own: `notched = Boolean(startAdornment || filled || focused)`. There
  // is no adornment, so the gap is open while the box has the caret or a value.
  const linkNotched = linkFocused || linkDraft !== "";
  // draft-js's `_showPlaceholder`: a placeholder, and no text (`editorHasText`).
  // IME composition is the third clause there; a composing editor already
  // holds the composed characters in the DOM, so `hasText` answers it here.
  const showPlaceholder = Boolean(placeholder) && !hasText;

  return (
    <OutlinedField
      label={label}
      required={required}
      shrink
      error={error}
      helperText={helperText}
      disabled={disabled}
      focused={boxFocused}
      // The donor draws this one field's outline by hand rather than letting
      // MUI's `OutlinedInput` do it — see `boxFocused` above and the prop.
      outline="hand-rolled"
      htmlFor={editorId}
      messageId={messageId}
      className={className}
    >
      {/* The donor's geometry, which is three nested boxes and not the one it
        looks like: `InputContainer` fixes a **120px** floor
        (`RichTextField.tsx:79`), `EditorContainer` insets everything by
        **16.5px / 14px** — MUI's own outlined-input padding
        (`RichTextEditor.tsx:124`) — and the toolbar and the text share that
        inset. So the rule under the toolbar stops 14px short of the outline on
        both sides rather than running edge to edge, and the editor is whatever
        is left of the 120: it gets no floor of its own, it takes the slack. */}
      <div ref={box} className="flex min-h-[120px] w-full flex-col items-stretch self-stretch">
        {/* `EditorContainer` — the donor's inner box, and **the inset belongs to
          it**, not to the 120px one around it (`RichTextEditor.tsx:124-125`).
          That is not a detail: this element is what the frame's hover reads
          (`RichTextField.tsx:56-57`), and a border box includes its own padding,
          so in modsy the outline darkens the moment the pointer crosses the
          frame anywhere above the slack — the 14px gutters included. Hanging the
          inset on the outer box instead left a 14px band down each side and
          16.5px across the top where the pointer did nothing.

          It is content-height, so the slack below the last line is
          `InputContainer`'s: hovering it does not darken the outline, and
          pressing it paints the frame blue (the document `mousedown` above)
          without placing a caret.

          **A block, not a flex column**, as `EditorContainer` is: in normal flow
          the toolbar's 8px `marginBottom` collapses with the first block's own
          top margin, so a list first sits 16px under the rule. A flex column
          made the editor a flex item, which contains its children's margins —
          8 + 16, and the field 1.5px taller than modsy's (#657, D7). */}
        {/* `pl-3 pr-4`, not `px-3.5`: the hand-rolled frame sits 2px right of
            its column, as the donor's fieldset does (`outlined-field.tsx`),
            and the donor's text keeps its 14px inset from the column itself —
            so 12px from the shifted frame on the left and 16px on the right. */}
        <div data-slot="outlined-field-body" className="w-full py-[16.5px] pr-4 pl-3">
          {/* The toolbar takes no `disabled`: the donor's never reaches it — see
          the prop. Only the Link control has an off state, and it is about the
          selection, not the form. */}
          <div
            data-slot="rich-text-toolbar"
            // The donor's toolbar: 5px between controls, no padding of its own, a
            // light rule beneath it with 8px under that, and `position: relative`
            // so the link box can hang off the Link control (`ToolbarContainer` /
            // `LinkInputContainer`).
            // No wrap: `ToolbarContainer` is `display: flex; flexDirection: row`
            // and nothing else (`RichTextEditor.tsx:334-341`), so a narrow column
            // squeezes the donor's row rather than folding it onto two. Wrapping
            // here reflowed the toolbar at around a 375px viewport, where the
            // donor's still sits on one line. And no `alignItems`: every item
            // has its own height, so each sits at the row's top — the row is
            // taller than the glyphs, because of the link box below.
            className="border-lennar-toolbar-rule relative mb-2 flex items-start gap-[5px] border-b"
          >
            {INLINE_COMMANDS.map(({ label: name, command, icon }) => (
              <ToolbarButton
                key={command}
                label={name}
                disabled={false}
                active={active.has(command)}
                onPress={() => run(command)}
              >
                {icon}
              </ToolbarButton>
            ))}
            <ToolbarDivider />
            {BLOCK_COMMANDS.map(({ label: name, command, icon }) => (
              <ToolbarButton
                key={command}
                label={name}
                disabled={false}
                active={active.has(command)}
                onPress={() => run(command)}
              >
                {icon}
              </ToolbarButton>
            ))}
            <ToolbarDivider />
            {/* The Link control and its card are one region for the outside-click
              rule below. The donor's ref is on the card alone, because its
              `ToolbarIcon` opens on `click` — a gesture that finishes after the
              `mousedown` has been and gone. This port's toolbar opens on
              `mousedown` (which is how it keeps the selection), so the very
              press that opens the card would otherwise be the press that closes
              it. Either way the donor's observable result is the same: pressing
              Link while the card is open leaves it open.

              **A plain block around an inline control**, as `LinkFormatContainer`
              is around its inline-block `SvgIcon` (`RichTextEditor.tsx:361-363`):
              the glyph sits in a line box of the field's 16px/24px text, so the
              box is taller than the glyph, and it is what stands the donor's
              toolbar 31.5px tall rather than 25 — 30.5px here, measured live on
              both stacks (#657, D7). How deep the line runs below the glyph is
              the font's metrics and the inline box's baseline, which is why the
              control is an inline-block `<button>`: in this app's Inter Variable
              a bare inline `<svg>` builds a 25px line where modsy's static Inter
              builds 30.5, and the button lands on modsy's. */}
            <div ref={linkBox} className="relative">
              <ToolbarButton
                inline
                label="Link"
                disabled={!hasSelection}
                active={active.has("link")}
                onPress={openLinkDraft}
              >
                <MuiLinkIcon />
              </ToolbarButton>
              {linkOpen ? (
                <div
                  data-slot="rich-text-link-box"
                  // The donor's `LinkInputContainer`, to the pixel: a 200px card
                  // hanging directly below the Link control, its two buttons
                  // right-aligned.
                  className="absolute bottom-0 left-0 z-10 flex w-[200px] translate-y-full flex-col items-end gap-1.5 bg-white p-1.5 shadow-[1px_1px_10px_#d3d3d3]"
                >
                  {/* `LinkInput` — a bare `position: relative` div
                  (`RichTextEditor.tsx:383-385`), which is the box the floated
                  label is positioned against. */}
                  <div className="relative w-full">
                    {/* **The donor's label is MUI's floated one, not a caption
                    above the box.** `<InputLabel>Link URL</InputLabel>`
                    (`RichTextEditor.tsx:298`) reads as bare, and this file used
                    to take it that way and draw a 16px `text.secondary` line in
                    flow above the field — which made the card 105px against the
                    donor's 82 and left the gap in the border holding nothing.
                    It is not bare: the `OutlinedInput` beside it is an
                    `InputBase` inside the **Description** field's
                    `FormControl`, so the two share that context and the label
                    takes `MuiInputLabel-outlined` with MUI's default
                    `shrink = filled || focused` — off this very input, since it
                    is the one reporting focus and value into the context.

                    Measured on the running donor rather than read off the JSX,
                    which is what got it wrong the first time: autofocused, the
                    label is `translate(14px, -9px) scale(0.75)` in
                    `rgb(0, 60, 119)` over a 2px navy line with the legend at
                    `max-width: 100%`; blurred while still empty, it drops back
                    to `translate(14px, 16px)` at 16px `text.secondary` and the
                    gap shuts to `0.01px`.

                    **It rests at 16px, not at the 9px a 40px field would
                    suggest.** The `OutlinedInput` is `size='small'`, but
                    `InputLabel` reads its size from the `FormControl`, and the
                    Description's sets none — so the label takes the *medium*
                    resting offset over a small box
                    (`MUI_RESTING_FIELD_LABEL_BOX`). The donor's own
                    inconsistency, and visible: the resting label is taller than
                    the field is padded for.

                    **No red.** `error` is set on the `OutlinedInput` itself
                    (`:307`) and never reaches the `FormControl`, so a refused
                    address reddens the outline and leaves the label navy —
                    measured with `not a url` typed in. */}
                    <span
                      data-shrink={linkNotched}
                      className={cn(
                        linkNotched ? MUI_FIELD_LABEL : MUI_FIELD_LABEL_RESTING,
                        MUI_FIELD_LABEL_TRANSITION,
                        linkFocused && MUI_FIELD_LABEL_FOCUSED,
                      )}
                    >
                      Link URL
                    </span>
                    <input
                      aria-label="Link URL"
                      aria-invalid={linkInvalid || undefined}
                      autoFocus
                      value={linkDraft}
                      onFocus={() => setLinkFocused(true)}
                      onBlur={() => setLinkFocused(false)}
                      onChange={(event) => {
                        setLinkDraft(event.target.value.trim());
                        setLinkEdited(true);
                      }}
                      // **Two keys the donor does not have** — the owner's
                      // ruling on #410 (D1), a named parity break taken with
                      // the source in front of us. Modsy's box is a bare
                      // `<OutlinedInput>` with `value`, `onChange` and nothing
                      // else (`RichTextEditor.tsx:300-308`): no `onKeyDown`,
                      // no `onKeyPress`, no form of its own, so the only way
                      // to confirm a link there is the check mark.
                      //
                      // Both keys are stopped dead rather than merely acted
                      // on, and that is the point. This box sits inside the
                      // Request Item form — the port mirrors the donor's
                      // `<form id>` plus its associated `type="submit"` button
                      // — so an Enter left to fall through implicitly submits
                      // that form and raises eight validation errors instead
                      // of applying the address, and an Escape left to bubble
                      // closes the whole dialog and loses the form. Handling
                      // no keys is only harmless in the donor because there is
                      // nothing above its box to catch them.
                      //
                      // Escape closes without clearing, the same bargain the
                      // outside click strikes: it is a dismissal, not a
                      // cancel. Only the cross clears (`cancelLink`).
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          event.stopPropagation();
                          // `applyLink` carries the `linkInvalid` gate, so a
                          // refused address is refused here too — one gate,
                          // shared with the check mark.
                          applyLink();
                        }
                        // Escape is *not* here — it cannot be. See the
                        // `window`-capture effect above `cancelLink`.
                      }}
                      // The box itself is the donor's
                      // `<OutlinedInput size='small' />` (`:301-308`), which the
                      // theme does not override — so it is the same 40px
                      // control as the menus' search box and the two inline
                      // lookup fields, and it wears the shared shape rather
                      // than a fourth spelling of it. It was drawn at 32px with
                      // 14px ink and 8px sides: the same miss round 11 swept
                      // out of the other three.
                      className={cn(MUI_FIELD_NOTCHED_TRIGGER, "w-full")}
                    />
                    {/* The line and the label's hole (`field-notch.tsx`) —
                    this surface was the fifth, hand-rolled, and #492's sweep
                    of the other four missed it. Rendered unconditionally
                    rather than on `linkNotched`, because an element that is
                    added and removed cannot transition; after the input, so it
                    reads the input's state as its `peer`.

                    **An errored field is one red outline, in every state.**
                    MUI paints error and focus as a single 2px `error.main`
                    line, and emits `&.Mui-error .notchedOutline` *after* both
                    its `:hover` and its focused rule at equal specificity — so
                    the red survives the pointer and the caret alike
                    (`OutlinedInput.js:53-82`). `MUI_FIELD_OUTLINE_PEER` stands
                    its hover and focus colours down under the `aria-invalid`
                    this box sets, so the red simply wins. */}
                    <FieldNotch
                      label="Link URL"
                      notched={linkNotched}
                      className={MUI_FIELD_OUTLINE_PEER}
                    />
                  </div>
                  {/* `LinkInputButtonsContainer` — 10px, not the 6px its parent
                  uses (`RichTextEditor.tsx:387`). */}
                  <div className="flex items-center gap-2.5">
                    <LinkCardButton
                      label="Cancel link"
                      disabled={false}
                      // `LinkInputCloseButton` — `color: 'gray'`, hovering to
                      // `primary.dark` (`RichTextEditor.tsx:392-400`).
                      className="text-lennar-icon-resting hover:text-lennar-blue-hover"
                      onPress={cancelLink}
                    >
                      <MuiClearIcon />
                    </LinkCardButton>
                    <LinkCardButton
                      label="Apply link"
                      // Empty is allowed: it is the unlink gesture, as the donor
                      // has it. Only a malformed address closes the button.
                      disabled={linkInvalid}
                      // `LinkInputConfirmButton` — `primary.main` at **rest**,
                      // `primary.dark` on hover (`:402-412`). It is the card's
                      // affirmative action and the donor paints it as one; drawn
                      // from `toolbarButtonClasses` it rested in the same grey as
                      // the ✕ it outranks.
                      className="text-lennar-blue-600 hover:text-lennar-blue-hover"
                      onPress={applyLink}
                    >
                      <MuiCheckIcon />
                    </LinkCardButton>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          {/* draft-js's `.DraftEditor-root`: a plain block around the
              placeholder and the editor (`DraftEditor.react.js:500+`). An
              empty list's or heading's top margin collapses up through it, so
              the placeholder's static position rides down onto that block's
              line, as modsy's does — as a bare sibling of the editor it stayed
              under the toolbar. */}
          <div data-slot="rich-text-root">
            {showPlaceholder ? (
              // **The donor's override is the whole style** — `Draft.css` is
              // never loaded, so its `width`, `z-index` and focus colours do not
              // apply: `color: gray`, `font-size: 14px`, `position: absolute`,
              // `pointer-events: none` (`RichTextEditor.tsx:127-132`), at its
              // static position, which is the editor's first line. 14px against
              // the field's 16px is the donor's own number; the line height is
              // the body's inherited 1.5. The inner box is draft-js's
              // `-inner`, at `white-space: pre-wrap`
              // (`DraftEditorPlaceholder.react.js:38-53`). Removed from the DOM
              // when hidden, as draft-js's is, and carries no ARIA of its own
              // (`DraftEditorPlaceholder.react.js:38-53`): page text a screen
              // reader reads as well as the editor's description.
              <div
                data-slot="rich-text-placeholder"
                className="text-lennar-icon-resting pointer-events-none absolute text-[14px] leading-[1.5]"
              >
                <div id={placeholderId} className="whitespace-pre-wrap">
                  {placeholder}
                </div>
              </div>
            ) : null}
            <div
              id={editorId}
              ref={editor}
              role="textbox"
              aria-multiline="true"
              aria-label={label}
              aria-required={required || undefined}
              aria-invalid={error ? true : undefined}
              // The message's id, and the placeholder's while it shows — draft-js
              // substitutes `placeholder-{key}` into `ariaDescribedBy` only when
              // `_showPlaceholder()` holds (`DraftEditor.react.js:494-497`).
              aria-describedby={
                [error || helperText ? messageId : null, showPlaceholder ? placeholderId : null]
                  .filter(Boolean)
                  .join(" ") || undefined
              }
              contentEditable
              suppressContentEditableWarning
              data-testid={testId}
              onInput={emit}
              onBlur={emit}
              onPaste={(event) => {
                event.preventDefault();
                insertTransferred(event.clipboardData);
              }}
              // A fragment dragged *within* the editor is a move, and draft-js
              // leaves it to `moveText` with its styling intact
              // (`DraftEditorDragHandler.js:106-108`) — so the browser's own move is
              // let through and only the `input` it raises is picked up.
              onDragStart={() => {
                internalDrag.current = true;
              }}
              onDragEnd={() => {
                internalDrag.current = false;
              }}
              onDrop={(event) => {
                if (internalDrag.current) return;
                event.preventDefault();
                insertTransferred(event.dataTransfer, {
                  asText: true,
                  at: { x: event.clientX, y: event.clientY },
                });
              }}
              className={cn(
                "w-full text-base leading-6 outline-none",
                // **What the read side wears, minus the paragraph rule.** Draft-js
                // renders a heading as a real `<h1>` and a list as a real `<ul>`
                // (`DefaultDraftBlockRenderMap`) with no stylesheet over them, so
                // the donor's *editor* shows UA headings and UA lists — while
                // preflight here was flattening a migrated heading to body copy and
                // indenting its lists 24px instead of 40. The same content was
                // changing shape between the field and the page.
                RICH_TEXT_PAINT,
              )}
            />
          </div>
        </div>
      </div>
    </OutlinedField>
  );
}

/**
 * A toolbar command.
 *
 * `onMouseDown` with the default prevented, *and* `onKeyDown` — the command acts
 * on the editor's live selection, and a mousedown landing on a button would
 * collapse it before any click fired, but a keyboard never sends a mousedown at
 * all. Handling only the pointer left every command unreachable from the
 * keyboard while looking, from a pointer test, entirely fine.
 *
 * Not `onClick`: a click arrives after the mousedown that already ran the
 * command, so the two together would run it twice.
 */
function ToolbarButton({
  label,
  disabled,
  active,
  onPress,
  inline = false,
  children,
}: {
  label: string;
  disabled: boolean;
  active: boolean;
  onPress: () => void;
  /** Inline-level, on a line box of its own — the Link control's donor shape. */
  inline?: boolean;
  children: ReactNode;
}) {
  return (
    // **No tab stop, and no tooltip.** Every control in this toolbar is a bare
    // `styled(SvgIcon)` carrying an `onMouseDown`
    // (`RichTextEditor.tsx:182-202,289-295`, and `:392,:403` for the link
    // card's two), and MUI's `SvgIcon` emits `focusable="false"` whenever no
    // `titleAccess` is passed — which none of them do (`SvgIcon.js:155,157`).
    // So in the source the toolbar is pointer-only: Tab runs Item Link → the
    // editor, no control takes a focus ring, and none carries a name for the
    // browser to draw a tooltip from. Drawn as `<button aria-label title>`,
    // this port put seven focus stops and seven native tooltips on a screen
    // that has neither — nine with the link card open.
    //
    // Bold, italic and underline still answer their browser shortcuts inside
    // the `contentEditable`, exactly as draft-js's `handleKeyCommand` answers
    // them in the donor (`RichTextEditor.tsx:95-96`). The other four have no
    // keyboard route here and none there.
    //
    // The **name stays**, which the donor's `aria-hidden` icons have not got:
    // it is inaudible to every modsy user who can see the screen, and dropping
    // it would be shipping a deliberate screen-reader regression to match an
    // accident of MUI's `SvgIcon` defaults. Named for the owner rather than
    // decided here.
    <button
      type="button"
      // No tab stop, as the source has none — which is about the *Tab* order,
      // not about who can reach the control (see `onClick` below).
      tabIndex={-1}
      aria-label={label}
      disabled={disabled}
      onMouseDown={(event) => {
        // The press, not the click: `preventDefault` here is what stops the
        // `contentEditable` losing the selection the command is about to act
        // on, and it has to happen before focus moves.
        event.preventDefault();
        onPress();
      }}
      // …and the activation that arrives with no press behind it. A control
      // drawn as `<button aria-label>` rather than an `aria-hidden` icon is
      // reachable by assistive tech, which activates it with a synthetic click
      // — `detail: 0`, and no `mousedown` at all. A mouse fires both, and its
      // click carries a click-count, so this is the one that must not run
      // twice.
      onClick={(event) => {
        if (event.detail !== 0) return;
        onPress();
      }}
      aria-pressed={active}
      className={cn(toolbarButtonClasses(active), inline && "inline-block")}
    >
      {children}
    </button>
  );
}

/**
 * One of the link card's two icons — not a toolbar command.
 *
 * The donor draws these as their own styled components, `LinkInputCloseButton`
 * and `LinkInputConfirmButton` (`RichTextEditor.tsx:392-412`): bare 24px icons
 * with no ground of any kind, each carrying its own resting colour. So they do
 * not take `toolbarButtonClasses` — which paints the chip the owner ruled onto
 * the *commands* (D7) and rests everything in grey — and the pair spans the
 * donor's 58px rather than 74px of chips.
 *
 * Pointer-only for the same reason the commands are: `styled(CloseIcon)` is an
 * `SvgIcon` at `focusable="false"`.
 */
function LinkCardButton({
  label,
  disabled,
  className,
  onPress,
  children,
}: {
  label: string;
  disabled: boolean;
  className: string;
  onPress: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-label={label}
      disabled={disabled}
      onMouseDown={(event) => {
        // The press, not the click: `preventDefault` here is what stops the
        // `contentEditable` losing the selection the command is about to act
        // on, and it has to happen before focus moves.
        event.preventDefault();
        onPress();
      }}
      // …and the activation that arrives with no press behind it. A control
      // drawn as `<button aria-label>` rather than an `aria-hidden` icon is
      // reachable by assistive tech, which activates it with a synthetic click
      // — `detail: 0`, and no `mousedown` at all. A mouse fires both, and its
      // click carries a click-count, so this is the one that must not run
      // twice.
      onClick={(event) => {
        if (event.detail !== 0) return;
        onPress();
      }}
      className={cn(
        "flex cursor-pointer items-center justify-center transition-colors",
        // **Only the colour branches.** `LinkInputConfirmButton` sets
        // `cursor: 'pointer'` unconditionally and switches `color` on
        // `disabled` (`RichTextEditor.tsx:403-410`) — unlike its sibling
        // `ToolbarIcon`, which does branch the cursor (`:351`). So an
        // unappliable address still hovers under the hand here.
        disabled ? "text-lennar-icon-disabled hover:text-lennar-icon-disabled" : className,
      )}
    >
      {children}
    </button>
  );
}

/**
 * The donor's 24px vertical rule between the toolbar's three groups.
 *
 * MUI's `<Divider orientation='vertical' sx={{ height: '24px' }} />`, so its
 * colour is `divider` and its margins are none: the 5px the toolbar's own `gap`
 * puts on each side is the whole separation.
 */
function ToolbarDivider() {
  return <span aria-hidden="true" className="bg-lennar-toolbar-divider h-6 w-px" />;
}
